/**
 * Envío MC-2 · Configuración → Claude: qué proyectos puede modificar Claude,
 * hasta cuándo, revocar y volver a la copia previa al primer cambio.
 */
import { useCallback, useEffect, useState } from 'react';
import { RotateCcw, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { claudeAccessService, grantLabel } from '../../services/claudeAccessService';
import '../../styles/claude-access.css';

export default function ClaudeAccessPanel() {
  const [grants, setGrants] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [g, p] = await Promise.all([claudeAccessService.grants(), claudeAccessService.pending()]);
      setGrants(g); setPending(p);
    } catch (_e) {
      setGrants([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, ok) => {
    setBusy(id);
    try { await fn(); toast.success(ok); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo completar.'); }
    finally { setBusy(null); }
  };

  const decide = (r, action) => act(r.id,
    () => (action === 'deny' ? claudeAccessService.deny(r.id) : claudeAccessService.approve(r.id, action)),
    action === 'deny' ? 'Pedido rechazado.' : 'Permiso concedido.');

  return (
    <div className="ca-panel" data-testid="claude-access-panel">
      {pending.length > 0 && (
        <section className="ca-panel-block">
          <h3>Pedidos sin responder</h3>
          {pending.map((r) => (
            <div key={r.id} className="ca-row">
              <div className="ca-row-main">
                <strong>{r.project_name}</strong>
                {r.reason && <span className="ca-row-sub">«{r.reason}»</span>}
              </div>
              <div className="ca-row-actions">
                <button type="button" className="ca-btn ca-btn-primary" disabled={busy === r.id}
                        onClick={() => decide(r, '1h')}>1 hora</button>
                <button type="button" className="ca-btn" disabled={busy === r.id}
                        onClick={() => decide(r, 'project')}>Siempre</button>
                <button type="button" className="ca-btn ca-btn-ghost" disabled={busy === r.id}
                        onClick={() => decide(r, 'deny')}>Rechazar</button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="ca-panel-block">
        <h3>Proyectos que Claude puede modificar</h3>
        {grants === null && <p className="ca-empty">Cargando…</p>}
        {grants && grants.length === 0 && (
          <p className="ca-empty">
            Ninguno. Cuando Claude necesite editar un proyecto hecho en la app, te va a
            aparecer un aviso en el estudio para que lo apruebes.
          </p>
        )}
        {grants && grants.map((g) => (
          <div key={g.id} className="ca-row" data-testid="claude-access-grant">
            <div className="ca-row-main">
              <strong>{g.project_name}</strong>
              <span className="ca-row-sub">{grantLabel(g)}{g.connection_name ? ` · ${g.connection_name}` : ''}</span>
            </div>
            <div className="ca-row-actions">
              <button type="button" className="ca-btn" disabled={busy === g.id || !g.has_snapshot}
                      title={g.has_snapshot ? 'Vuelve el proyecto a como estaba antes del primer cambio de Claude'
                        : 'Claude todavía no hizo cambios con este permiso'}
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        if (window.confirm(`¿Volver «${g.project_name}» a como estaba antes de los cambios de Claude?`)) {
                          act(g.id, () => claudeAccessService.restore(g.id), 'Proyecto restaurado.');
                        }
                      }}>
                <RotateCcw size={14} /> Restaurar copia
              </button>
              <button type="button" className="ca-btn ca-btn-ghost" disabled={busy === g.id}
                      onClick={() => act(g.id, () => claudeAccessService.revoke(g.id), 'Permiso revocado.')}>
                <ShieldOff size={14} /> Revocar
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
